/*
 * AuthService — Prueft Login-Status und cached User-Daten
 *
 * Kommuniziert mit /api/auth/me und speichert das Ergebnis
 * in localStorage fuer Offline-Zugriff.
 */

const CACHE_KEY = 'carbisdale_user'

// LoginPage liest dieses Flag nach einem Session-Verlust-Reload und zeigt
// einen erklaerenden Hinweis ("Session expired") statt kommentarlosem Rauswurf.
export const SESSION_EXPIRED_KEY = 'carbisdale_session_expired'

// Reload-Schutz: bei mehreren parallelen 401-Antworten (Sync + Agent + Ping
// koennen gleichzeitig schlagen) darf der Reload nur einmal feuern.
let _sessionLostHandled = false

/**
 * Wird gerufen, wenn der Server eine geschuetzte Route mit 401 ablehnt — JWT
 * abgelaufen oder serverseitig gecleart. Loescht den User-Cache und reloaded
 * die Seite, damit `init()` den LoginPage-Pfad in `main.js` erreicht.
 *
 * IndexedDB bleibt erhalten; Sync-Pending-Items werden nach Wieder-Login
 * automatisch erneut versucht.
 */
export function handleSessionLost() {
    if (_sessionLostHandled) return
    _sessionLostHandled = true
    try { localStorage.removeItem(CACHE_KEY) } catch { /* ignore */ }
    try { localStorage.setItem(SESSION_EXPIRED_KEY, '1') } catch { /* ignore */ }
    // Reload triggert init() → checkAuth() → LoginPage.show()
    if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
        window.location.reload()
    }
}

/** Test-Hook: setzt das Flag zurueck, damit Tests handleSessionLost() mehrfach pruefen koennen. */
export function _resetSessionLostHandledForTests() {
    _sessionLostHandled = false
}

export class AuthService {
    constructor() {
        this.user = null       // { id, email, name, avatarUrl, role }
        this.isLoggedIn = false
        this.isOffline = false
        this._onOnline = null  // gebundener 'online'-Listener (watchOnline)
    }

    /**
     * B4 (System-Review 2026-06-10): Offline gestartete Session heilt
     * sich selbst — sobald der Browser wieder Netz meldet, prueft
     * checkAuth() erneut und setzt isOffline zurueck. Ohne das blieben
     * alle Sync-Gates (enqueue, flushNow, Poll) fuer die GESAMTE Session
     * zu und spaetere Server-Edits gewaennen per LWW.
     * Wird einmalig von main.js nach der Instanziierung aufgerufen.
     */
    watchOnline() {
        if (this._onOnline) return   // idempotent
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
        this._onOnline = () => {
            if (this.isOffline) this.checkAuth()
        }
        window.addEventListener('online', this._onOnline)
    }

    /** Entfernt den 'online'-Listener wieder (Tests/Cleanup). */
    unwatchOnline() {
        if (!this._onOnline) return
        window.removeEventListener('online', this._onOnline)
        this._onOnline = null
    }

    /**
     * Prueft den Login-Status beim Server.
     *
     * @returns {Object|null|'blocked'}
     *   - Object: User-Daten (eingeloggt)
     *   - null: Nicht eingeloggt (Server erreichbar)
     *   - 'blocked': Account gesperrt
     *   Bei Netzwerkfehler: cached User oder null
     */
    async checkAuth() {
        try {
            const res = await fetch('/api/auth/me')

            if (res.ok) {
                const user = await res.json()
                this.user = user
                this.isLoggedIn = true
                this.isOffline = false
                this._cacheUser(user)
                return user
            }

            if (res.status === 403) {
                const data = await res.json().catch(() => ({}))
                if (data.blocked) {
                    return 'blocked'
                }
            }

            // 401 oder anderer Fehler → nicht eingeloggt
            this.user = null
            this.isLoggedIn = false
            this._clearCache()
            return null

        } catch (err) {
            // Netzwerkfehler → Offline-Modus mit Cache
            console.warn('Auth-Check fehlgeschlagen (offline?):', err.message)
            const cached = this._getCachedUser()
            if (cached) {
                this.user = cached
                this.isLoggedIn = true
                this.isOffline = true
                return cached
            }
            // Kein Cache → kann nichts tun
            return null
        }
    }

    /**
     * Gibt den gecachten User zurueck (fuer Offline-Modus).
     */
    _getCachedUser() {
        try {
            const data = localStorage.getItem(CACHE_KEY)
            return data ? JSON.parse(data) : null
        } catch {
            return null
        }
    }

    /**
     * Cached User-Daten in localStorage.
     */
    _cacheUser(user) {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(user))
        } catch {
            // localStorage voll — ignorieren
        }
    }

    /**
     * Loescht den User-Cache.
     */
    _clearCache() {
        try {
            localStorage.removeItem(CACHE_KEY)
        } catch {
            // ignorieren
        }
    }

    /**
     * Loescht alle lokalen Daten (fuer Logout).
     */
    clearAllLocalData() {
        this._clearCache()
        this.user = null
        this.isLoggedIn = false
    }
}
